import type { AnnotationFormat } from "@perceptron/api/apiContracts/chatCompletions";

import { DocumentInputContentV1 } from "@demo/types/documentContent";

import type { ComposeTemplate, TemplateAsset } from "./types";

const PERCEPTRON_MK_MODEL = "perceptron-mk1.5";

type VideoTemplateInput = {
  slug: string;
  title: string;
  description: string;
  subdescription?: string;
  poster: TemplateAsset;
  video: TemplateAsset;
  prompt: string;
  featured?: boolean;
  /** Output format and reasoning the template turns on; `setModelConfiguration` applies them on selection. */
  annotationFormat?: AnnotationFormat;
  enableThinking?: boolean;
  enableAudioInVideo?: boolean;
  /** Sampling temperature the template pins; omitted leaves the model's own default. */
  temperature?: number;
};

/**
 * Builds a video-backed `TemplateDefinition`. Centralizes the boilerplate
 * around `DocumentInputContentV1` (media wrapper, paragraph),
 * the model id used for video templates, and the poster vs. video asset
 * split (poster lives in the `images/` bucket, video in `videos/`).
 */
export function defineVideoTemplate({
  slug,
  title,
  description,
  subdescription,
  poster,
  video,
  prompt,
  featured,
  annotationFormat,
  enableThinking,
  enableAudioInVideo,
  temperature,
}: VideoTemplateInput): ComposeTemplate {
  return {
    mode: "compose",
    slug,
    version: 1,
    title,
    description,
    subdescription,
    thumbnailUuid: poster.uuid,
    assets: [video],
    featured,
    request: {
      model: PERCEPTRON_MK_MODEL,
      system: [],
      user: [
        {
          type: "media",
          value: [
            {
              type: "video",
              value: {
                uuid: video.uuid,
                extension: video.extension,
              },
            },
          ],
        },
        {
          type: "paragraph",
          value: { type: "text", value: prompt },
        },
      ],
      outputAnnotations: false,
      temperature,
      variables: {},
      annotationFormat: annotationFormat ?? null,
      enableThinking: enableThinking ?? false,
      ...(enableAudioInVideo === undefined ? {} : { enableAudioInVideo }),
    } satisfies DocumentInputContentV1,
  };
}
